import { useState, useEffect, useRef } from 'react';
import { api } from '../api';

const INTROS = {
  professional: "Hi. Let's talk about your matches — what's landing, what isn't. I can also update your profile directly if anything needs changing.",
  casual: "Hey — what's going on? Dating stuff, life stuff, whatever you want to talk about.",
  psychedelic: "I've been thinking about your chart. There's something I want to show you.",
};

function ToolBadge({ action }) {
  const label = action.field.replace(/_/g, ' ');
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      background: 'rgba(201,168,76,0.08)', border: '0.5px solid rgba(201,168,76,0.3)',
      borderRadius: 2, padding: '5px 10px', marginTop: 8,
      fontSize: 11, color: 'var(--gold)', letterSpacing: 1,
    }}>
      ✓ Updated {label}
    </div>
  );
}

function Bubble({ role, content, toolAction }) {
  const isUser = role === 'user';
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: isUser ? 'flex-end' : 'flex-start',
      marginBottom: 18,
    }}>
      {!isUser && (
        <div style={{
          width: 28, height: 28, borderRadius: '50%',
          background: 'rgba(201,168,76,0.12)', border: '0.5px solid rgba(201,168,76,0.35)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 11, fontWeight: 400, color: 'var(--gold)',
          letterSpacing: 1, marginBottom: 6,
        }}>
          P
        </div>
      )}
      <div style={{
        maxWidth: '82%',
        background: isUser ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.03)',
        border: `0.5px solid ${isUser ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.06)'}`,
        padding: '12px 16px',
        fontSize: 14,
        fontWeight: 300,
        color: isUser ? 'rgba(255,255,255,0.75)' : 'rgba(255,255,255,0.88)',
        lineHeight: 1.65,
        fontFamily: isUser ? 'var(--font-ui)' : 'var(--font-display)',
        fontStyle: isUser ? 'normal' : 'italic',
      }}>
        {content}
      </div>
      {toolAction && <ToolBadge action={toolAction} />}
    </div>
  );
}

function ThinkingBubble() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginBottom: 18 }}>
      <div style={{
        width: 28, height: 28, borderRadius: '50%',
        background: 'rgba(201,168,76,0.12)', border: '0.5px solid rgba(201,168,76,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, color: 'var(--gold)', letterSpacing: 1, marginBottom: 6,
      }}>
        P
      </div>
      <div style={{
        background: 'rgba(255,255,255,0.03)', border: '0.5px solid rgba(255,255,255,0.06)',
        padding: '14px 18px', display: 'flex', gap: 5, alignItems: 'center',
      }}>
        {[0, 1, 2].map(i => (
          <div key={i} style={{
            width: 5, height: 5, borderRadius: '50%',
            background: 'rgba(201,168,76,0.5)',
            animation: `paulaDot 1.2s ease-in-out ${i * 0.2}s infinite`,
          }} />
        ))}
      </div>
    </div>
  );
}

export default function PaulaChat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mode, setMode] = useState(() => localStorage.getItem('paula_mode') || 'professional');
  const [isPlus, setIsPlus] = useState(false);
  const [recording, setRecording] = useState(false);
  const [voiceMode, setVoiceMode] = useState(false);
  const [voiceReply, setVoiceReply] = useState(null); // { text, audioBase64 }
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);

  const switchMode = (m) => {
    setMode(m);
    localStorage.setItem('paula_mode', m);
  };

  useEffect(() => {
    api.get('/ai/paula/history')
      .then(data => { setMessages(data.messages); setLoading(false); })
      .catch(() => setLoading(false));
    api.get('/profile').then(p => setIsPlus(p.subscription_tier === 'plus')).catch(() => {});
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, thinking]);

  const send = async () => {
    const text = input.trim();
    if (!text || thinking) return;
    setInput('');
    setError('');
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setThinking(true);

    try {
      const data = await api.post('/ai/paula/chat', { message: text, mode });
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: data.reply, tool_action: data.tool_action },
      ]);
    } catch (err) {
      if (err.message === 'AI not configured') {
        setError('AI service is not configured on the server.');
      } else if (err.message === 'AI temporarily unavailable') {
        setError('AI service returned an error — check server logs.');
      } else {
        setError(`Error: ${err.message}`);
      }
    } finally {
      setThinking(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const startRecording = async () => {
    if (!isPlus || thinking) return;
    setVoiceReply(null);
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
      const recorder = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        sendAudio(mimeType);
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setRecording(true);
    } catch {
      setError('Microphone access denied');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  };

  const toggleRecording = () => {
    if (recording) stopRecording();
    else startRecording();
  };

  const switchToVoice = () => {
    if (!isPlus) { setError('Voice chat requires Matchmaker+'); return; }
    setVoiceMode(true);
    setError('');
  };

  const playAudio = (base64) => {
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
      const audio = new Audio(`data:audio/mp3;base64,${base64}`);
      audioRef.current = audio;
      audio.play().catch(() => {}); // silently fail if autoplay blocked — user can tap replay
    } catch {}
  };

  const sendAudio = async (mimeType) => {
    const blob = new Blob(audioChunksRef.current, { type: mimeType });
    if (blob.size < 500) return;
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = reader.result.split(',')[1];
      setError('');
      setVoiceReply(null);
      setThinking(true);
      try {
        const data = await api.post('/ai/paula/voice', { audio: base64, mode });
        setMessages(prev => [
          ...prev,
          { role: 'user', content: data.transcript },
          { role: 'assistant', content: data.reply, tool_action: data.tool_action },
        ]);
        // Show reply in the voice overlay and attempt audio playback
        setVoiceReply({ text: data.reply, audioBase64: data.audio || null });
        if (data.audio) playAudio(data.audio);
      } catch (err) {
        setError(`Error: ${err.message}`);
      } finally {
        setThinking(false);
      }
    };
    reader.readAsDataURL(blob);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 80 }}>
        <span className="spinner" />
      </div>
    );
  }

  const showIntro = messages.length === 0;

  return (
    <>
      <style>{`
        @keyframes paulaDot {
          0%, 80%, 100% { opacity: 0.2; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1); }
        }
        @keyframes paulaMicPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(255,60,60,0.25); }
          50% { box-shadow: 0 0 0 20px rgba(255,60,60,0); }
        }
        @keyframes paulaMicIdle {
          0%, 100% { box-shadow: 0 0 0 0 rgba(201,168,76,0.15); }
          50% { box-shadow: 0 0 0 14px rgba(201,168,76,0); }
        }
      `}</style>

      {/* Voice mode full-screen overlay */}
      {voiceMode && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 100,
          background: 'rgba(0,0,0,0.97)',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          gap: 32,
        }}>
          {/* Paula label */}
          <div style={{
            fontSize: 11, letterSpacing: 3, textTransform: 'uppercase',
            color: 'rgba(201,168,76,0.5)', fontFamily: 'var(--font-ui)',
          }}>
            Voice chat with Paula
          </div>

          {/* Big mic */}
          <button
            onClick={thinking ? undefined : toggleRecording}
            disabled={thinking}
            style={{
              width: 120, height: 120, borderRadius: '50%',
              background: recording
                ? 'rgba(255,60,60,0.12)'
                : 'rgba(201,168,76,0.07)',
              border: `1.5px solid ${recording ? 'rgba(255,60,60,0.55)' : 'rgba(201,168,76,0.35)'}`,
              color: recording ? 'rgba(255,100,100,0.85)' : 'rgba(201,168,76,0.8)',
              fontSize: 44, cursor: thinking ? 'default' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.25s',
              animation: thinking ? 'none'
                : recording ? 'paulaMicPulse 1.4s ease-in-out infinite'
                : 'paulaMicIdle 2.5s ease-in-out infinite',
            }}
          >
            {thinking
              ? <span className="spinner" style={{ width: 32, height: 32 }} />
              : '🎙'}
          </button>

          {/* Paula's reply — shown in overlay so user can read it */}
          {voiceReply ? (
            <div style={{
              maxWidth: 300, textAlign: 'center',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
            }}>
              <div style={{
                fontSize: 15, fontFamily: 'var(--font-display)', fontStyle: 'italic',
                color: 'rgba(255,255,255,0.85)', lineHeight: 1.65, fontWeight: 300,
                padding: '0 8px',
              }}>
                {voiceReply.text}
              </div>
              {voiceReply.audioBase64 && (
                <button
                  onClick={() => playAudio(voiceReply.audioBase64)}
                  style={{
                    background: 'none', border: '0.5px solid rgba(201,168,76,0.3)',
                    color: 'rgba(201,168,76,0.6)', fontSize: 11, letterSpacing: 1,
                    padding: '7px 20px', cursor: 'pointer', fontFamily: 'var(--font-ui)',
                  }}
                >
                  ▶ play again
                </button>
              )}
            </div>
          ) : (
            /* Status label when no reply yet */
            <div style={{
              fontSize: 13, letterSpacing: 1, fontWeight: 300,
              color: recording ? 'rgba(255,100,100,0.65)' : 'var(--very-muted)',
              fontFamily: 'var(--font-ui)', transition: 'color 0.2s',
              textAlign: 'center',
            }}>
              {thinking
                ? 'Paula is listening...'
                : recording
                ? 'Recording  —  tap again to send'
                : 'Tap to speak'}
            </div>
          )}

          {error && (
            <div style={{ fontSize: 12, color: 'rgba(255,90,70,0.7)', textAlign: 'center', padding: '0 40px' }}>
              {error}
            </div>
          )}

          {/* Exit */}
          <button
            onClick={() => {
              if (recording) stopRecording();
              setVoiceMode(false);
              setError('');
            }}
            style={{
              position: 'absolute', bottom: 60,
              background: 'none', border: '0.5px solid rgba(255,255,255,0.1)',
              color: 'var(--very-muted)', fontSize: 12, letterSpacing: 1,
              padding: '10px 28px', cursor: 'pointer',
              fontFamily: 'var(--font-ui)',
            }}
          >
            exit voice chat
          </button>
        </div>
      )}

      {/* Mode toggle + voice button */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px',
        borderBottom: '0.5px solid rgba(255,255,255,0.06)',
        flexShrink: 0,
      }}>
        {[
          { key: 'professional', label: 'Professional' },
          { key: 'casual',       label: 'Casual' },
          { key: 'psychedelic',  label: 'Psychedelic' },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => switchMode(key)}
            style={{
              padding: '5px 12px',
              borderRadius: 20,
              border: mode === key
                ? key === 'psychedelic'
                  ? '0.5px solid rgba(160,100,255,0.6)'
                  : '0.5px solid rgba(201,168,76,0.6)'
                : '0.5px solid rgba(255,255,255,0.1)',
              background: mode === key
                ? key === 'psychedelic'
                  ? 'rgba(160,100,255,0.1)'
                  : 'rgba(201,168,76,0.1)'
                : 'transparent',
              color: mode === key
                ? key === 'psychedelic' ? 'rgba(180,130,255,0.9)' : 'var(--gold)'
                : 'var(--very-muted)',
              fontSize: 11,
              letterSpacing: 0.5,
              cursor: 'pointer',
              transition: 'all 0.15s',
              flexShrink: 0,
            }}
          >
            {label}
          </button>
        ))}

        {/* Voice chat button — pushed to far right */}
        <button
          onClick={switchToVoice}
          style={{
            marginLeft: 'auto', flexShrink: 0,
            padding: '5px 12px', borderRadius: 20,
            border: isPlus ? '0.5px solid rgba(255,255,255,0.15)' : '0.5px solid rgba(255,255,255,0.07)',
            background: 'transparent',
            color: isPlus ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.15)',
            fontSize: 11, letterSpacing: 0.5,
            cursor: isPlus ? 'pointer' : 'default',
            transition: 'all 0.15s',
          }}
        >
          {isPlus ? '🎙 Voice' : '🔒 Voice'}
        </button>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 20px 12px', minHeight: 0 }}>
        {showIntro && <Bubble role="assistant" content={INTROS[mode] || INTROS.professional} />}
        {messages.map((m, i) => (
          <Bubble key={i} role={m.role} content={m.content} toolAction={m.tool_action} />
        ))}
        {thinking && <ThinkingBubble />}
        {error && (
          <div style={{ fontSize: 12, color: 'rgba(255,90,70,0.7)', textAlign: 'center', marginBottom: 12 }}>
            {error}
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input area */}
      <div style={{
        flexShrink: 0,
        borderTop: '0.5px solid rgba(255,255,255,0.06)',
        background: '#000',
      }}>
        {voiceMode ? (
          /* ── Voice mode: full-screen overlay ── */
          <>
          </>
        ) : (
          /* ── Text mode ── */
          <div style={{ padding: '12px 16px 72px', display: 'flex', flexDirection: 'column', gap: 0 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
              <textarea
                ref={inputRef}
                rows={1}
                value={input}
                onChange={e => {
                  setInput(e.target.value);
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 96) + 'px';
                }}
                onKeyDown={handleKey}
                placeholder="Message Paula..."
                style={{
                  flex: 1, background: 'rgba(255,255,255,0.04)',
                  border: '0.5px solid rgba(255,255,255,0.12)',
                  color: 'var(--text)', fontFamily: 'var(--font-ui)',
                  fontSize: 14, fontWeight: 300,
                  padding: '10px 14px', resize: 'none', outline: 'none',
                  lineHeight: 1.5, maxHeight: 96, overflowY: 'auto',
                }}
              />
              <button
                onClick={send}
                disabled={!input.trim() || thinking}
                style={{
                  width: 40, height: 40, flexShrink: 0,
                  background: input.trim() && !thinking ? 'rgba(201,168,76,0.12)' : 'transparent',
                  border: `0.5px solid ${input.trim() && !thinking ? 'rgba(201,168,76,0.5)' : 'rgba(255,255,255,0.1)'}`,
                  color: input.trim() && !thinking ? 'var(--gold)' : 'var(--very-muted)',
                  fontSize: 16, cursor: input.trim() && !thinking ? 'pointer' : 'default',
                  transition: 'all 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                ↑
              </button>
            </div>

          </div>
        )}
      </div>
    </>
  );
}
